//
// # Bounding Boxes
//

import { Point } from "./point";

export interface Size {
  width: number;
  height: number;
}

export interface Bbox {
  min: Point;
  max: Point;
}
export const Bbox = {
  // Clone a `Bbox`
  clone: (bbox: Bbox): Bbox => ({
    min: Point.clone(bbox.min),
    max: Point.clone(bbox.max),
  }),

  // Get the center of a `Bbox`
  center: (bbox: Bbox): Point => ({
    x: (bbox.min.x + bbox.max.x) / 2,
    y: (bbox.min.y + bbox.max.y) / 2,
  }),

  // Get the size of a `Bbox`
  size: (bbox: Bbox): Size => ({
    width: bbox.max.x - bbox.min.x,
    height: bbox.max.y - bbox.min.y,
  }),

  // Get the size and center of a `Bbox`
  sizeAndCenter: (bbox: Bbox): { size: Size; center: Point } => ({
    size: Bbox.size(bbox),
    center: Bbox.center(bbox),
  }),

  // Create a new `Bbox` from a size and center point.
  fromSizeAndCenter: (size: Size, center: Point): Bbox => ({
    min: Point.new(center.x - size.width / 2, center.y - size.height / 2),
    max: Point.new(center.x + size.width / 2, center.y + size.height / 2),
  }),

  // Create a `Bbox` with one point at the origin and the other at `size`
  fromSize: (size: Size): Bbox => {
    return {
      min: Point.origin(),
      max: { x: size.width, y: size.height },
    };
  },

  empty: (): Bbox => ({ min: Point.origin(), max: Point.origin() }),

  // Create from a list of Points
  fromPoints: (...points: Array<Point>): Bbox => {
    if (points.length === 0) {
      return Bbox.empty();
    }
    const [first, ...rest] = points;
    const min = Point.clone(first);
    const max = Point.clone(first);
    for (const point of rest) {
      min.x = Math.min(min.x, point.x);
      min.y = Math.min(min.y, point.y);
      max.x = Math.max(max.x, point.x);
      max.y = Math.max(max.y, point.y);
    }
    return { min, max };
  },

  fromTwoPoints: (pts: [Point, Point]): Bbox => {
    return {
      min: { x: Math.min(pts[0].x, pts[1].x), y: Math.min(pts[0].y, pts[1].y) },
      max: { x: Math.max(pts[0].x, pts[1].x), y: Math.max(pts[0].y, pts[1].y) },
    };
  },

  // Test whether `point` is inside `bbox`.
  // Points on the boundary return `true`.
  contains: (bbox: Bbox, point: Point): boolean => {
    return (
      point.x >= bbox.min.x &&
      point.x <= bbox.max.x &&
      point.y >= bbox.min.y &&
      point.y <= bbox.max.y
    );
  },

  translate: (bbox: Bbox, delta: Point): Bbox => ({
    min: Point.add(bbox.min, delta),
    max: Point.add(bbox.max, delta),
  }),

  // Create a new `Bbox` including an existing `Bbox` and a new `Point`.
  include: (bbox: Bbox, pt: Point): Bbox => {
    return {
      min: {
        x: Math.min(bbox.min.x, pt.x),
        y: Math.min(bbox.min.y, pt.y),
      },
      max: {
        x: Math.max(bbox.max.x, pt.x),
        y: Math.max(bbox.max.y, pt.y),
      },
    };
  },
  // Determine the union of an array of bounding boxes
  getBboxUnion: (bbox_arr: Array<Bbox>): Bbox => {
    if (bbox_arr.length === 0) {
      return Bbox.empty();
    }
    // Initialize from the first element (not Bbox.empty()) to avoid
    // always including the origin (0,0) in the result.
    let bbox_union = bbox_arr[0];
    for (let i = 1; i < bbox_arr.length; i++) {
      bbox_union = Bbox.encompassingBoth(bbox_union, bbox_arr[i]);
    }
    return bbox_union;
  },

  // Return a new Bbox which just covers the two given Bboxes.
  encompassingBoth: (left: Bbox, right: Bbox): Bbox => {
    return {
      min: {
        x: Math.min(left.min.x, right.min.x),
        y: Math.min(left.min.y, right.min.y),
      },
      max: {
        x: Math.max(left.max.x, right.max.x),
        y: Math.max(left.max.y, right.max.y),
      },
    };
  },

  // Boolean indication of whether two Bboxes intersect
  intersects: (a: Bbox, b: Bbox): boolean => {
    return (
      a.min.x <= b.max.x &&
      a.max.x >= b.min.x &&
      a.min.y <= b.max.y &&
      a.max.y >= b.min.y
    );
  },

  // Get the intersection of two Bboxes, or null if they don't intersect
  intersection: (a: Bbox, b: Bbox): Bbox | null => {
    if (!Bbox.intersects(a, b)) {
      return null;
    }
    return {
      min: {
        x: Math.max(a.min.x, b.min.x),
        y: Math.max(a.min.y, b.min.y),
      },
      max: {
        x: Math.min(a.max.x, b.max.x),
        y: Math.min(a.max.y, b.max.y),
      },
    };
  },

  eq: (a: Bbox, b: Bbox): boolean => {
    return Point.eq(a.min, b.min) && Point.eq(a.max, b.max);
  },

  // Expand a Bbox by a scale factor from its center
  expandByFactor: (bbox: Bbox, factor: number): Bbox => {
    const center = Bbox.center(bbox);
    const size = Bbox.size(bbox);
    const newSize = {
      width: size.width * factor,
      height: size.height * factor,
    };
    return Bbox.fromSizeAndCenter(newSize, center);
  },
};
